import { Injectable, inject } from '@angular/core';
import { ModifierDescripteursSyllabusObjetMaquetteCommand } from '@app/features/editeur/models/commands/modifier-descripteurs-syllabus-objet-maquette-command';
import { ModifierLienEnfantCommand } from '@app/features/editeur/models/commands/modifier-lien-enfant';
import { RetirerEnfantCommand } from '@app/features/editeur/models/commands/retirer-enfant-command';
import { RetirerPointInscriptionAdministrativeCommand } from '@app/features/editeur/models/commands/retirer-point-inscription-administrative-command';
import { SupprimerMutualiseCommand } from '@app/features/editeur/models/commands/supprimer-mutualise-command';
import { SupprimerObjetMaquetteCommand } from '@app/features/editeur/models/commands/supprimer-objet-maquette-command';
import { ValiderPartieBasseCommand } from '@app/features/editeur/models/commands/valider-partie-basse-command';
import { ValiderPartieHauteCommand } from '@app/features/editeur/models/commands/valider-partie-haute-command';
import { CommandHistoryService } from '@app/features/editeur/services/command-history.service';
import { StructureService } from '@app/shared/structure/structure.service';
import {
    CreerFormationRequest,
    CreerGroupementRequest,
    CreerObjetFormationRequest,
    DescripteursContexte,
    DescripteursEnquete,
    DescripteursFormationSyllabus,
    DescripteursGroupementSyllabus,
    DescripteursObjetFormationSyllabus,
    DescripteursObjetMaquette,
    Enfant,
    FormatsEnseignementRequest
} from '@pcscol/odf-api-v1-angular';
import { AjouterEnfantCommand } from '../models/commands/ajouter-enfant-command';
import { Command, HistoryDecoratorCommand } from '../models/commands/command';
import { CommandEvents } from '../models/commands/command-events';
import { CreerObjetMaquetteCommand } from '../models/commands/creer-objet-maquette-command';
import { InactiverPointInscriptionAdministrativeCommand } from '../models/commands/inactiver-point-inscription-administrative-command';
import { ModifierCodeObjetMaquetteCommand } from '../models/commands/modifier-code-objet-maquette-command';
import { ModifierDescripteurObjetMaquetteCommand } from '../models/commands/modifier-descripteur-objet-maquette-command';
import { ModifierDescripteursContexteObjetMaquetteCommand } from '../models/commands/modifier-descripteurs-contexte-objet-maquette-command';
import { ModifierDescripteursEnqueteObjetMaquetteCommand } from '../models/commands/modifier-descripteurs-enquete-objet-maquette-command';
import { ModifierFormatsEnseignementsCommand } from '../models/commands/modifier-formats-enseignements-command';
import { OrdonnerEnfantCommand } from '../models/commands/ordonner-enfant-command';
import { ObjetMaquette } from '../models/objet-maquette.model';
import { ObjetMaquetteService } from './objet-maquette.service';
import { RendreMutualiseCommand } from '../models/commands/rendre-mutualise-command';
import { DeclarerPointInscriptionAdministrativeCommand } from '../models/commands/declarer-point-inscription-administrative-command copy';
import { ReactiverPointInscriptionAdministrativeCommand } from '../models/commands/reactiver-point-inscription-administrative-command';

@Injectable({
    providedIn: 'root'
})
export class CommandFactoryService {
    private objetsMaquetteService = inject(ObjetMaquetteService);
    private structureService = inject(StructureService);
    private historyService = inject(CommandHistoryService);
    private events = inject(CommandEvents);

    ajouterEnfantCommand(id: string, version: string, enfant: Enfant): Command<ObjetMaquette> {
        return this.decorate(
            new AjouterEnfantCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                enfant,
                false
            )
        );
    }

    creerObjetMaquetteCommand(creerObjetMaquetteRequest: CreerFormationRequest | CreerGroupementRequest | CreerObjetFormationRequest) {
        return this.decorate(
            new HistoryDecoratorCommand(
                new CreerObjetMaquetteCommand(
                    this.structureService.current$.value,
                    this.events,
                    this.objetsMaquetteService,
                    creerObjetMaquetteRequest
                ),
                this.historyService
            )
        );
    }

    declarerPointInscriptionAdministrativeCommand(id: string, version: string, contexteId: string) {
        return this.decorate(
            new HistoryDecoratorCommand(
                new DeclarerPointInscriptionAdministrativeCommand(
                    this.structureService.current$.value,
                    id,
                    version,
                    this.events,
                    this.objetsMaquetteService,
                    contexteId,
                    false
                ),
                this.historyService
            )
        );
    }

    inactiverPointInscriptionAdministrativeCommand(id: string, version: string, contexteId: string) {
        return this.decorate(
            new HistoryDecoratorCommand(
                new InactiverPointInscriptionAdministrativeCommand(
                    this.structureService.current$.value,
                    id,
                    version,
                    this.events,
                    this.objetsMaquetteService,
                    contexteId
                ),
                this.historyService
            )
        );
    }

    reactiverPointInscriptionAdministrativeCommand(id: string, version: string, contexteId: string) {
        return this.decorate(
            new HistoryDecoratorCommand(
                new ReactiverPointInscriptionAdministrativeCommand(
                    this.structureService.current$.value,
                    id,
                    version,
                    this.events,
                    this.objetsMaquetteService,
                    contexteId
                ),
                this.historyService
            )
        );
    }

    modifierFormatsEnseignementsCommand(
        id: string,
        version: string,
        oldFormatsEnseignements: FormatsEnseignementRequest,
        newFormatsEnseignements: FormatsEnseignementRequest
    ) {
        return this.decorate(
            new ModifierFormatsEnseignementsCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                oldFormatsEnseignements,
                newFormatsEnseignements
            )
        );
    }

    modifierCodeObjetMaquetteCommand(id: string, version: string, oldCode: string, newCode: string) {
        return this.decorate(
            new ModifierCodeObjetMaquetteCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                oldCode,
                newCode
            )
        );
    }

    modifierDescripteurObjetMaquetteCommand(
        id: string,
        version: string,
        oldDescripteursObjetMaquette: DescripteursObjetMaquette,
        newDescripteursObjetMaquette: DescripteursObjetMaquette
    ) {
        return this.decorate(
            new ModifierDescripteurObjetMaquetteCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                oldDescripteursObjetMaquette,
                newDescripteursObjetMaquette
            )
        );
    }

    modifierDescripteursContexteObjetMaquetteCommand(
        id: string,
        version: string,
        contexteId: string,
        oldDescripteursContexte: DescripteursContexte,
        newDescripteursContexte: DescripteursContexte
    ) {
        return this.decorate(
            new ModifierDescripteursContexteObjetMaquetteCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                contexteId,
                oldDescripteursContexte,
                newDescripteursContexte
            )
        );
    }

    modifierDescripteursEnqueteObjetMaquetteCommand(
        id: string,
        version: string,
        oldDescripteursEnquete: DescripteursEnquete,
        newDescripteursEnquete: DescripteursEnquete
    ) {
        return this.decorate(
            new ModifierDescripteursEnqueteObjetMaquetteCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                oldDescripteursEnquete,
                newDescripteursEnquete
            )
        );
    }

    ordonnerEnfantCommand(idParent: string, idEnfant: string, idsEnfantsAvant: string[], idsEnfantsApres: string[]) {
        return this.decorate(
            new OrdonnerEnfantCommand(idParent, idEnfant, this.events, this.objetsMaquetteService, idsEnfantsAvant, idsEnfantsApres)
        );
    }

    validerPartieHauteCommand(id: string, contexteId: string) {
        return this.decorate(new ValiderPartieHauteCommand(id, this.events, this.objetsMaquetteService, contexteId));
    }

    validerPartieBasseCommand(id: string, contexteId: string) {
        return this.decorate(new ValiderPartieBasseCommand(id, this.events, this.objetsMaquetteService, contexteId));
    }

    supprimerObjetMaquetteCommand(id: string, version: string, ignoreConflict: boolean) {
        return this.decorate(
            new SupprimerObjetMaquetteCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                ignoreConflict
            )
        );
    }

    supprimerMutualiseCommand(id: string, version: string) {
        return this.decorate(
            new SupprimerMutualiseCommand(this.structureService.current$.value, id, version, this.events, this.objetsMaquetteService)
        );
    }

    retirerPointInscriptionAdministrativeCommand(id: string, version: string, contexteId: string, ignoreConflict: boolean) {
        return this.decorate(
            new RetirerPointInscriptionAdministrativeCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                contexteId,
                ignoreConflict
            )
        );
    }

    retirerEnfantCommand(id: string, version: string, enfant: Enfant, ignoreConflict: boolean) {
        return this.decorate(
            new RetirerEnfantCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                enfant,
                ignoreConflict
            )
        );
    }

    rendreMutualiseCommand(id: string, version: string) {
        return this.decorate(
            new RendreMutualiseCommand(this.structureService.current$.value, id, version, this.events, this.objetsMaquetteService)
        );
    }

    modifierLienEnfantCommand(id: string, version: string, idEnfant: string, temoinObligatoire: boolean) {
        return this.decorate(
            new ModifierLienEnfantCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                idEnfant,
                temoinObligatoire
            )
        );
    }

    modifierDescripteursSyllabusObjetMaquetteCommand(
        id: string,
        version: string,
        oldDescripteursSyllabus: DescripteursFormationSyllabus | DescripteursObjetFormationSyllabus | DescripteursGroupementSyllabus,
        newDescripteursSyllabus: DescripteursFormationSyllabus | DescripteursObjetFormationSyllabus | DescripteursGroupementSyllabus
    ) {
        return this.decorate(
            new ModifierDescripteursSyllabusObjetMaquetteCommand(
                this.structureService.current$.value,
                id,
                version,
                this.events,
                this.objetsMaquetteService,
                oldDescripteursSyllabus,
                newDescripteursSyllabus
            )
        );
    }

    private decorate<E>(command: Command<E>): Command<E> {
        return new HistoryDecoratorCommand(command, this.historyService);
    }
}
